/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Add, LockOutlined } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createEmptyContentRequest, MEDIA_TYPES } from '../Task.model';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';
import SortableTaskContentRequest from './SortableTaskContentRequest';

export const MAX_CONTENT_REQUESTS = 5;

const styles = {
  section: adminFormStyles.section,
  sectionTitle: adminFormStyles.sectionTitle,
  sectionDescription: adminFormStyles.sectionDescription,
  helperText: adminFormStyles.helperText,
  lockedAlert: {
    alignItems: 'flex-start',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: '#656d80',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#656d80', mt: 0.25 },
  },
  addButton: {
    borderColor: '#a7b1c0',
    borderRadius: '8px',
    borderStyle: 'dashed',
    color: colors.base.walmart_blue,
    fontSize: '14px',
    fontWeight: 700,
    minHeight: 48,
    textTransform: 'none',
    transition: 'transform 120ms cubic-bezier(0.2, 0, 0, 1)',
    '&:hover': {
      backgroundColor: colors.base.barely_blue,
      borderColor: colors.base.walmart_blue,
      borderStyle: 'dashed',
    },
    '&:active': { transform: 'scale(0.96)' },
  },
};

export function reorderContentRequests(contentRequests, activeId, overId) {
  const oldIndex = contentRequests.findIndex(
    (request) => request.ui_id === activeId
  );
  const newIndex = contentRequests.findIndex(
    (request) => request.ui_id === overId
  );

  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return contentRequests;
  }
  return arrayMove(contentRequests, oldIndex, newIndex);
}

export default function TaskContentRequestsForm({
  contentRequests,
  errors,
  isReadOnly,
  onChange,
  summaryError,
}) {
  // Keep one stable blank row for display until the user edits it.
  const [placeholderRequest] = useState(() =>
    createEmptyContentRequest({ media_type: MEDIA_TYPES.PHOTO })
  );
  const displayedRequests = contentRequests.length
    ? contentRequests
    : [placeholderRequest];
  const hasReachedMax = displayedRequests.length >= MAX_CONTENT_REQUESTS;
  const hasLockedMediaType = contentRequests.some((request) => request.id);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function updateItem(index, field, value) {
    // The form displays one blank row for a new task without adding it to the
    // payload. Materialize that row only after the admin edits a field.
    const nextRequests = contentRequests.length
      ? [...contentRequests]
      : [placeholderRequest];

    // Replace the edited item without mutating the parent form's state.
    nextRequests[index] = {
      ...nextRequests[index],
      [field]: value,
    };
    onChange(nextRequests);
  }

  function appendItem() {
    // Include the initially displayed virtual row so the new row appears on
    // the first click.
    onChange([
      ...displayedRequests,
      createEmptyContentRequest({ media_type: MEDIA_TYPES.PHOTO }),
    ]);
  }

  function removeItem(index) {
    const nextRequests = [...contentRequests];
    nextRequests.splice(index, 1);
    onChange(nextRequests);
  }

  function reorderItems({ active, over }) {
    if (!over || active.id === over.id) return;

    onChange(reorderContentRequests(contentRequests, active.id, over.id));
  }

  return (
    <Paper component="section" variant="outlined" sx={styles.section}>
      <Stack spacing="20px" padding="20px">
        <Box>
          <Typography component="h2" sx={styles.sectionTitle}>
            Content items
          </Typography>
          <Typography sx={styles.sectionDescription}>
            Each item becomes a capture card in the associate&apos;s task
            carousel.
          </Typography>
        </Box>

        {summaryError && <Alert severity="error">{summaryError}</Alert>}

        {isReadOnly && (
          <Alert
            icon={<LockOutlined fontSize="small" />}
            severity="info"
            sx={styles.lockedAlert}
          >
            <strong>Content items are locked</strong> because this request has
            ended.
          </Alert>
        )}

        {!isReadOnly && hasLockedMediaType && (
          <Alert
            icon={<LockOutlined fontSize="small" />}
            severity="info"
            sx={styles.lockedAlert}
          >
            <strong>Media type is locked</strong> for saved items. Remove one
            and add a new item to change its media type.
          </Alert>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={reorderItems}
        >
          <SortableContext
            items={displayedRequests.map((request) => request.ui_id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing="20px">
              {displayedRequests.map((contentRequest, index) => (
                <SortableTaskContentRequest
                  key={contentRequest.ui_id}
                  contentRequest={contentRequest}
                  index={index}
                  canRemove={contentRequests.length > 0}
                  errors={errors[contentRequest.ui_id] || {}}
                  isReadOnly={isReadOnly}
                  onRemove={removeItem}
                  onUpdate={updateItem}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>

        {!isReadOnly && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<Add />}
            onClick={appendItem}
            disabled={hasReachedMax}
            sx={styles.addButton}
          >
            Add content item
          </Button>
        )}
        {!isReadOnly && hasReachedMax && (
          <Typography sx={styles.helperText}>
            A request can have up to {MAX_CONTENT_REQUESTS} content items.
            Remove one to add another.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

TaskContentRequestsForm.propTypes = {
  contentRequests: PropTypes.arrayOf(
    PropTypes.shape({
      ui_id: PropTypes.string.isRequired,
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      media_type: PropTypes.oneOf(['', ...Object.values(MEDIA_TYPES)]),
    })
  ).isRequired,
  errors: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
      media_type: PropTypes.string,
    })
  ),
  isReadOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  summaryError: PropTypes.string,
};

TaskContentRequestsForm.defaultProps = {
  errors: {},
  isReadOnly: false,
  summaryError: null,
};
